import React, { useRef } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { EyeIcon, HeartIcon, UsersIcon, MicIcon, GlobeIcon, BookOpenIcon } from 'lucide-react';
type ContentItem = {
  icon: React.ReactNode;
  title: string;
  description: string;
};
const items: ContentItem[] = [{
  icon: <EyeIcon className="w-6 h-6" data-id="element-1" />,
  title: 'Hechos históricos que fueron silenciados',
  description: 'Documentación rigurosa de la represión, la corrupción sistémica y las decisiones que destruyeron la nación más rica de América Latina.'
}, {
  icon: <HeartIcon className="w-6 h-6" data-id="element-2" />,
  title: 'La crisis humanitaria desde adentro',
  description: 'Escasez de medicamentos, hambre, colapso de servicios básicos. No como estadística — sino como experiencia humana vivida.'
}, {
  icon: <GlobeIcon className="w-6 h-6" data-id="element-3" />,
  title: 'La caída final del régimen',
  description: 'Un análisis profundo del colapso político, las traiciones internas y el momento en que el mundo finalmente no pudo seguir mirando.'
}, {
  icon: <UsersIcon className="w-6 h-6" data-id="element-4" />,
  title: 'Voces de víctimas, migrantes y familias',
  description: 'Testimonios reales de quienes lo perdieron todo y aun así encontraron la fuerza para seguir. Sus palabras son el corazón de este libro.'
}, {
  icon: <MicIcon className="w-6 h-6" data-id="element-5" />,
  title: 'Lo que el mundo no quiso ver',
  description: 'La complicidad internacional, los medios que callaron y los gobiernos que miraron hacia otro lado mientras un pueblo entero sufría.'
}, {
  icon: <BookOpenIcon className="w-6 h-6" data-id="element-6" />,
  title: 'Profundo pero accesible para todos',
  description: 'Escrito para ser leído por cualquier persona, no solo académicos. Una narrativa que engancha desde la primera página hasta la última.'
}];
export function BookContents() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-80px'
  });
  return <section ref={ref} className="w-full bg-[#111111] py-24 px-6" data-id="element-7">
      <div className="max-w-5xl mx-auto" data-id="element-8">
        {/* Header */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.7
      }} className="text-center mb-16" data-id="element-9">
          <p className="text-[#CF9B2E] uppercase tracking-[0.3em] text-sm font-bold mb-4 font-['Lato']" data-id="element-10">
            Dentro del libro
          </p>
          <h2 className="text-4xl md:text-5xl font-bold text-[#F5F0E8] mb-6" data-id="element-11">
            Cinco razones para leerlo
          </h2>
          <div className="gold-divider" data-id="element-12" />
        </motion.div>

        {/* Grid */}
        <div className="flex flex-wrap justify-center gap-6" data-id="element-13">
          {/* Item 1 */}
          <motion.div initial={{
          opacity: 0,
          y: 40
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.6,
          delay: 0.1
        }} className="bg-[#0a0a0a] border border-[#1e1e1e] p-8 rounded-sm group w-full md:w-[calc(50%-12px)] lg:w-[calc(33.333%-16px)]" data-id="element-14">
            <div className="text-[#CF9B2E] mb-4" data-id="element-15">{items[0].icon}</div>
            <h3 className="text-xl font-bold text-[#F5F0E8] mb-3" data-id="element-16">
              {items[0].title}
            </h3>
            <p className="text-[#9A9080] leading-relaxed text-sm" data-id="element-17">
              {items[0].description}
            </p>
          </motion.div>

          {/* Item 2 */}
          <motion.div initial={{
          opacity: 0,
          y: 40
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.6,
          delay: 0.2
        }} className="bg-[#0a0a0a] border border-[#1e1e1e] p-8 rounded-sm group w-full md:w-[calc(50%-12px)] lg:w-[calc(33.333%-16px)]" data-id="element-18">
            <div className="text-[#C0392B] mb-4" data-id="element-19">{items[1].icon}</div>
            <h3 className="text-xl font-bold text-[#F5F0E8] mb-3" data-id="element-20">
              {items[1].title}
            </h3>
            <p className="text-[#9A9080] leading-relaxed text-sm" data-id="element-21">
              {items[1].description}
            </p>
          </motion.div>

          {/* Item 3 */}
          <motion.div initial={{
          opacity: 0,
          y: 40
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.6,
          delay: 0.3
        }} className="bg-[#0a0a0a] border border-[#1e1e1e] p-8 rounded-sm group w-full md:w-[calc(50%-12px)] lg:w-[calc(33.333%-16px)]" data-id="element-22">
            <div className="text-[#1A5276] mb-4" data-id="element-23">{items[2].icon}</div>
            <h3 className="text-xl font-bold text-[#F5F0E8] mb-3" data-id="element-24">
              {items[2].title}
            </h3>
            <p className="text-[#9A9080] leading-relaxed text-sm" data-id="element-25">
              {items[2].description}
            </p>
          </motion.div>

          {/* Item 4 */}

          {/* Item 5 */}
          <motion.div initial={{
          opacity: 0,
          y: 40
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.6,
          delay: 0.4
        }} className="bg-[#0a0a0a] border border-[#1e1e1e] p-8 rounded-sm group w-full md:w-[calc(50%-12px)] lg:w-[calc(33.333%-16px)]" data-id="element-26">
            <div className="text-[#C0392B] mb-4" data-id="element-27">{items[4].icon}</div>
            <h3 className="text-xl font-bold text-[#F5F0E8] mb-3" data-id="element-28">
              {items[4].title}
            </h3>
            <p className="text-[#9A9080] leading-relaxed text-sm" data-id="element-29">
              {items[4].description}
            </p>
          </motion.div>

          {/* Item 6 */}
          <motion.div initial={{
          opacity: 0,
          y: 40
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.6,
          delay: 0.6
        }} className="bg-[#0a0a0a] border border-[#1e1e1e] p-8 rounded-sm group w-full md:w-[calc(50%-12px)] lg:w-[calc(33.333%-16px)]" data-id="element-30">
            <div className="text-[#1A5276] mb-4" data-id="element-31">{items[5].icon}</div>
            <h3 className="text-xl font-bold text-[#F5F0E8] mb-3" data-id="element-32">
              {items[5].title}
            </h3>
            <p className="text-[#9A9080] leading-relaxed text-sm" data-id="element-33">
              {items[5].description}
            </p>
          </motion.div>
        </div>
      </div>
    </section>;
}